import { type MaybeRef, computed, toRef } from "vue";
import { useI18n } from "vue-i18n";

import { useMutation, useQuery } from "@tanstack/vue-query";

import { QUERY_KEYS } from "@/helpers/constants/queryKeys";
import { useNotification } from "@/hooks/notifications";
import { queryClient } from "@/query/queryClient";
import applicationsApiService from "@/services/modules/applications";

import type {
  ApplicationMetadata,
  ApplicationMetadataCreatePayload,
  ApplicationMetadataSyncPayload,
} from "@/models/applications";

interface UseMetadataQueryOptions {
  enabled?: MaybeRef<boolean>;
}

export const useMetadataQuery = (
  applicationId: MaybeRef<string | undefined>,
  options: UseMetadataQueryOptions = {}
) => {
  const { enabled = true } = options;
  const applicationIdRef = toRef(applicationId);

  return useQuery({
    queryKey: computed(() =>
      QUERY_KEYS.METADATA.byApplication(applicationIdRef.value ?? "")
    ),
    queryFn: async (): Promise<ApplicationMetadata[]> => {
      if (!applicationIdRef.value) {
        return [];
      }
      return applicationsApiService.getMetadata(applicationIdRef.value);
    },
    enabled: computed(() => {
      const enabledValue = toRef(enabled).value;
      return enabledValue && !!applicationIdRef.value;
    }),
  });
};

export const useCreateMetadataMutation = (
  applicationId: MaybeRef<string | undefined>
) => {
  const { t } = useI18n();
  const applicationIdRef = toRef(applicationId);

  return useMutation({
    mutationFn: async (
      payload: ApplicationMetadataCreatePayload
    ): Promise<ApplicationMetadata> => {
      if (!applicationIdRef.value) {
        throw new Error(t("COMMON.ERROR_OCCURRED"));
      }
      return applicationsApiService.createMetadata(
        applicationIdRef.value,
        payload
      );
    },
    onSuccess: (newMetadata) => {
      queryClient.setQueryData<ApplicationMetadata[]>(
        QUERY_KEYS.METADATA.byApplication(applicationIdRef.value ?? ""),
        (oldData) => (oldData ? [...oldData, newMetadata] : [newMetadata])
      );
    },
  });
};

export const useDeleteMetadataMutation = (
  applicationId: MaybeRef<string | undefined>
) => {
  const { t } = useI18n();
  const applicationIdRef = toRef(applicationId);

  return useMutation({
    mutationFn: async (metadataId: number): Promise<void> => {
      if (!applicationIdRef.value) {
        throw new Error(t("COMMON.ERROR_OCCURRED"));
      }
      await applicationsApiService.deleteMetadata(
        applicationIdRef.value,
        metadataId
      );
    },
    onMutate: async (deletedId) => {
      const queryKey = QUERY_KEYS.METADATA.byApplication(
        applicationIdRef.value ?? ""
      );
      await queryClient.cancelQueries({ queryKey });

      const previousData =
        queryClient.getQueryData<ApplicationMetadata[]>(queryKey);

      queryClient.setQueryData<ApplicationMetadata[]>(
        queryKey,
        (oldData) => oldData?.filter((item) => item.id !== deletedId) ?? []
      );

      return { previousData };
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          QUERY_KEYS.METADATA.byApplication(applicationIdRef.value ?? ""),
          context.previousData
        );
      }
    },
  });
};

export const useSyncMetadataMutation = (
  applicationId: MaybeRef<string | undefined>
) => {
  const { t } = useI18n();
  const { showMessage } = useNotification();
  const applicationIdRef = toRef(applicationId);

  return useMutation({
    mutationFn: async (
      payload: ApplicationMetadataSyncPayload
    ): Promise<void> => {
      if (!applicationIdRef.value) {
        throw new Error(t("COMMON.ERROR_OCCURRED"));
      }
      await applicationsApiService.syncMetadataWithFunders(
        applicationIdRef.value,
        payload
      );
    },
    onSuccess: () => {
      showMessage(t("DEALS.TRACKING.SYNCED_SUCCESSFULLY"), "success");
    },
  });
};
